import { useEffect, useState } from "react";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UserPlus, ShieldCheck, ShieldOff, UserX, UserCheck } from "lucide-react";

export default function Users() {
  const [rows, setRows] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "staff" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setRows(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Failed to load users"));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users/invite", form);
      toast.success(`Invited ${form.email}`);
      setInviteOpen(false);
      setForm({ email: "", role: "staff" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to invite"); }
    finally { setSaving(false); }
  };

  const setRole = async (u, role) => {
    if (!window.confirm(`Change ${u.email}'s role to "${role}"?`)) return;
    try { await api.post(`/users/${u.user_id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u) => {
    const action = u.is_active === false ? "activate" : "deactivate";
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${u.email}? This ${action === "deactivate" ? "revokes their sessions immediately." : "restores access."}`)) return;
    try { await api.post(`/users/${u.user_id}/${action}`, {}); toast.success(`User ${action}d`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-8">
      <PageHeader
        eyebrow="Access control"
        title="User management"
        description="Only listed users may sign in. Invite staff by email; they'll get access after their next Google sign-in."
        actions={<Button data-testid="invite-user-btn" onClick={() => setInviteOpen(true)} className="rounded-md bg-slate-900 hover:bg-slate-800"><UserPlus className="h-4 w-4 mr-1" />Invite user</Button>}
      />

      <div className="mt-6 border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[11px]">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Email</th>
              <th className="text-left px-4 py-2 font-semibold">Name</th>
              <th className="text-left px-4 py-2 font-semibold">Role</th>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
              <th className="text-left px-4 py-2 font-semibold">Invited</th>
              <th className="text-right px-4 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-500">No users yet.</td></tr>}
            {rows.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-900">{u.email}</td>
                <td className="px-4 py-3 text-slate-600">{u.name || "—"}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={u.role === "admin" ? "approved" : "not_started"} label={u.role} />
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={u.is_active === false ? "cancelled" : "active"} label={u.is_active === false ? "deactivated" : "active"} />
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{u.invited_by ? `by ${u.invited_by}` : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    {u.role !== "admin" ? (
                      <button data-testid={`promote-${u.user_id}`} onClick={() => setRole(u, "admin")} title="Make admin" className="p-1.5 text-slate-500 hover:text-blue-600 rounded"><ShieldCheck className="h-4 w-4" /></button>
                    ) : (
                      <button data-testid={`demote-${u.user_id}`} onClick={() => setRole(u, "staff")} title="Demote to staff" className="p-1.5 text-slate-500 hover:text-slate-900 rounded"><ShieldOff className="h-4 w-4" /></button>
                    )}
                    {u.is_active === false ? (
                      <button data-testid={`activate-${u.user_id}`} onClick={() => toggleActive(u)} title="Reactivate" className="p-1.5 text-slate-500 hover:text-emerald-600 rounded"><UserCheck className="h-4 w-4" /></button>
                    ) : (
                      <button data-testid={`deactivate-${u.user_id}`} onClick={() => toggleActive(u)} title="Deactivate" className="p-1.5 text-slate-500 hover:text-rose-600 rounded"><UserX className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-user-dialog">
          <DialogHeader><DialogTitle>Invite a user</DialogTitle></DialogHeader>
          <form onSubmit={invite} className="space-y-3">
            <div>
              <Label>Email *</Label>
              <Input required type="email" data-testid="invite-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@example.com" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">Staff cannot reveal Government Gateway passwords, Companies House auth codes, or view the Personal Vault.</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="invite-submit-btn" disabled={saving} className="bg-slate-900 hover:bg-slate-800">Send invite</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
